import React, { useState, useEffect } from "react";
import axios from "axios";
import Pusher from "pusher-js";
import {
  AppBar,
  Toolbar,
  Typography,
  IconButton,
  Box,
  Card,
  CircularProgress,
  BottomNavigation,
  BottomNavigationAction,
  Avatar,
} from "@mui/material";
import {
  Notifications as NotificationsIcon,
  Chat as ChatIcon,
  AccountCircle as AccountCircleIcon,
  CalendarToday as CalendarTodayIcon,
  Home as HomeIcon,
  Payment as PaymentIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import { toast, ToastContainer } from "react-toastify";

const AdminGetNotifs = () => {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const baseUrl = process.env.REACT_APP_APP_URL;

  const token = localStorage.getItem("CC_Token");
  const user = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null;
  const userId = user?.id || null;

  const fetchNotifications = async () => {
    if (!token || !userId) {
      console.error("Token or user ID is missing.");
      setLoading(false);
      return;
    }

    try {
      // Fetch notifications for the specific user
      const userNotifications = await axios.get(`${baseUrl}/api/notifications/user/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Fetch general notifications
      const generalNotifications = await axios.get(`${baseUrl}/api/notifications`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Combine notifications from both APIs
      const allNotifications = [...userNotifications.data, ...generalNotifications.data].sort(
        (a, b) => new Date(b.created_at) - new Date(a.created_at)
      );

      // Update state with all notifications
      setNotifications(allNotifications);
      console.log("Fetched notifications:", allNotifications); // Log notifications
    } catch (error) {
      console.error("Error fetching notifications:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!userId) {
      console.error("User ID is missing.");
      return;
    }

    fetchNotifications();

    if (Notification.permission === "default") {
      Notification.requestPermission();
    }

    const pusher = new Pusher("aebc2eec94386d5d25a3", {
      cluster: "mt1",
      forceTLS: true,
    });

    // Use one Pusher instance for both channels
    const globalChannel = pusher.subscribe("global.notifications");
    globalChannel.bind("NotificationCreated", (data) => {
      console.log("New notification on global channel:", data); // Log notification from global channel
      setNotifications((prev) => [...prev, data]);
      if (Notification.permission === "granted") {
        new Notification("New Notification", {
          body: data.message,
        });
      }
    });

    const userChannel = pusher.subscribe(`NotificationByUser.${userId}.notifications`);
    userChannel.bind("NotificationByUser", (data) => {
      console.log("New notification on user channel:", data); // Log notification from user-specific channel
      setNotifications((prev) => [...prev, data]);
      if (Notification.permission === "granted") {
        new Notification("New Notification", {
          body: data.message,
        });
      }
    });

    return () => {
      globalChannel.unbind_all();
      userChannel.unbind_all();
      pusher.unsubscribe("global.notifications");
      pusher.unsubscribe(`user.${userId}.notifications`);
      pusher.disconnect();
    };
  }, [userId]);

  const handleProfile = () => {
    navigate("/ProfileAdmin");
  };
  const handleNavigateToNotifications = () => {
    navigate("/AdminGetNotifs");
  };
  const handleLogout = () => {
      localStorage.removeItem("CC_Token");
      localStorage.removeItem("user");
      toast.success("Vous avez été déconnecté avec succès.", {
        position: "top-right",
        autoClose: 3000,
      });
      setTimeout(() => navigate("/login"), 4000); // Redirect after toast
    };
    

  return (
    <div className="app">
      {/* Toast Container */}
      <ToastContainer />

      {/* Header */}
      <AppBar position="fixed" sx={{ backgroundColor: "#003366" }}>
        <Toolbar>
          <Avatar
            alt="Logo"
            src={"../assets/cej.png"}
            sx={{ width: 40, height: 40, marginRight: "0.5rem" }}
          />
          <Typography
            variant="h6"
            sx={{
              flexGrow: 1,
              fontSize: { xs: "1rem", sm: "1.25rem" },
              color: "#ffffff",
            }}
          >
            Club Équestre Jafoura
          </Typography>
          <IconButton color="inherit" onClick={handleNavigateToNotifications}>
            <NotificationsIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleProfile}>
            <AccountCircleIcon />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout}>
            <ExitToAppIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          padding: "1rem",
          paddingTop: "70px", // Ensure content is not hidden behind the fixed header
          paddingBottom: "60px", // Ensure content is not hidden by the fixed footer
          backgroundColor: "#f5f5f5",
          overflowY: "auto", // Allow scrolling of content
        }}
      >
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: "200px" }}>
            <CircularProgress color="primary" />
            <Typography sx={{ marginLeft: "1rem", color: "#555" }}>
              Chargement des notifications...
            </Typography>
          </Box>
        ) : notifications.length > 0 ? (
          notifications.map((notification) => (
            <Card key={notification.id} sx={{ marginBottom: "0.5rem", padding: "1rem", borderRadius: "15px" }}>
              <Typography sx={{ fontWeight: 500, color: "#003366" }}>
                {notification.title}
              </Typography>
              <Typography sx={{ color: "#555555" }}>
                {notification.message}
              </Typography>
              <Typography sx={{ color: "#888888", marginTop: "0.5rem", fontSize: "0.8rem" }}>
                {new Date(notification.created_at).toLocaleString()}
              </Typography>
            </Card>
          ))
        ) : (
          <Typography>Aucune notification trouvée.</Typography>
        )}
      </Box>

      <Box sx={{ position: "fixed", bottom: 0, width: "100%" }}>
        <BottomNavigation
          sx={{
            backgroundColor: "#003366",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <BottomNavigationAction
            label="Calendrier"
            icon={<CalendarTodayIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
            sx={{ color: "#ffffff", minWidth: "auto" }}
            onClick={() => navigate("/AdminChrono")}
          />
       <BottomNavigationAction
                label="Home"
                icon={<HomeIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
                sx={{ color: "#ffffff", minWidth: "auto" }}
                onClick={() => navigate(user.role === "admin" ? "/admin" : "/InstructorHome")
                }
              />
              <BottomNavigationAction
                label="Paiement"
                icon={<PaymentIcon sx={{ fontSize: 30, color: "#ffffff" }} />}
                sx={{ color: "#ffffff", minWidth: "auto" }}
                  onClick={() => navigate(user.role === "admin" ? "/AdminPaiment" : "/InstructorHome")
                }
              />
            </BottomNavigation>
      </Box>
    </div>
  );
};

export default AdminGetNotifs;
